/**
 * Estimate Data Manager
 *
 * Manages a JSON data object that captures ALL user measurements and
 * configuration entered in the estimator BEFORE the Calculate Estimate
 * button is clicked.
 *
 * This object will eventually be submitted to a database with a GUID
 * to support sharing and saving/resuming estimates.
 *
 * Console testing:
 *   window.__dumpEstimateData()   – pretty-print current state
 *   window.__saveEstimateData()   – download config as JSON file
 *   window.__loadEstimateData()   – load config from JSON file
 */

import {
  CalculationMode,
  ProductColor,
  PanelOrientation,
  Currency,
  RoomDimensions,
  WallConfig,
  CeilingZone,
  WizardStep,
  ProjectInfo,
  GarageConfig,
  WizardState,
} from '../types';
import { ROOM_PRESETS } from '../data/presets';
import type { LoadStatePayload } from '../context/EstimatorContext';
import { tr } from '../i18n/standalone';

// ============================================================================
// Data Object Shape
// ============================================================================

export interface EstimateDataObject {
  /** Schema version for forward-compatibility checks */
  schemaVersion: number;

  /** Will hold a GUID when persisted to the database */
  id: string | null;

  /** ISO-8601 timestamp of when this snapshot was captured */
  snapshotTimestamp: string;

  /** Wizard navigation state */
  wizard: {
    currentStep: WizardStep;
    completedSteps: WizardStep[];
    /** True when the estimate had been calculated (user was viewing results) */
    hasResults: boolean;
  };

  /** Selected calculation mode */
  calculationMode: CalculationMode;

  /** Project / customer info (from lead capture) */
  projectInfo: ProjectInfo;

  /** Wall configuration (FullRoom mode) */
  wallsConfig: {
    color: ProductColor;
    orientation: PanelOrientation;
    slatWallEnabled: boolean;
    slatWallStartHeight: number;
    slatWallNumPanels: number;
    slatWallColor: ProductColor;
    slatWallCoveragePercent: number;
    usePresets: boolean;
    selectedPresetId: string | null;
  };

  /** Ceiling configuration */
  ceilingConfig: {
    include: boolean;
    color: ProductColor;
    zones: CeilingZone[] | undefined;
  };

  /** Room dimensions configuration */
  dimensionsConfig: {
    usePreset: boolean;
    presetId: string | undefined;
    customDimensions: RoomDimensions | undefined;
    /** Resolved dimensions (preset or custom) ready for calculation */
    resolvedDimensions: RoomDimensions;
  };

  /** Application / space configuration */
  applicationConfig: {
    spaceType: string;
    garage: GarageConfig & { onWallType: 'None' | 'LengthWall' | 'WidthWall' };
  };

  /** Individual wall definitions (IndividualWalls mode only) */
  individualWalls: WallConfig[] | undefined;

  /** Selected currency */
  currency: Currency;
}

// ============================================================================
// Module-level singleton & loader callback
// ============================================================================

let _currentData: EstimateDataObject | null = null;

/** Callback registered by EstimatorProvider to push loaded state into React */
type StateLoaderFn = (payload: LoadStatePayload, autoCalculate: boolean) => void;
let _stateLoader: StateLoaderFn | null = null;

/**
 * Called by EstimatorProvider on mount to register its dispatch bridge.
 */
export function registerStateLoader(loader: StateLoaderFn): void {
  _stateLoader = loader;
}

/**
 * Optional overrides for a save prompt. When provided, the modal pre-fills
 * its project-name input with `defaultName` and routes the confirmed name
 * through `onSave` instead of the singleton-based `performSave`. Used by
 * the shared-estimate view where the savable data lives outside the
 * wizard singleton.
 */
export interface SavePromptOptions {
  defaultName?: string;
  onSave?: (projectName: string) => void;
}

/** Callback registered by the UI to show a save-prompt modal */
type SavePromptFn = (options?: SavePromptOptions) => void;
let _savePrompt: SavePromptFn | null = null;

/**
 * Called by the component that owns the SaveModal to register the open callback.
 */
export function registerSavePrompt(prompt: SavePromptFn): void {
  _savePrompt = prompt;
}

/** Info passed to the load-confirm modal */
export interface LoadConfirmInfo {
  projectName: string;
  mode: string;
  dimensions: string;
  stepsComplete: number;
}

/** Callback registered by the UI to show a load-confirm modal */
type LoadConfirmFn = (info: LoadConfirmInfo) => Promise<boolean>;
let _loadConfirm: LoadConfirmFn | null = null;

/**
 * Called by the component that owns the LoadModal to register the confirm callback.
 */
export function registerLoadConfirm(confirm: LoadConfirmFn): void {
  _loadConfirm = confirm;
}

// ============================================================================
// Schema version — bump when EstimateDataObject shape changes
// ============================================================================
//
// v2 (Snap-In Trim J rollout): BillOfMaterials gained an optional jTrimsSnapIn
// field. v1 estimates load fine — the validator only rejects estimates whose
// schemaVersion is greater than this app's version. v1 BOMs are rendered
// as-saved (legacy J-Trim 1/2" + 3/4" sections); new estimates produce only
// the Snap-In section.
const SCHEMA_VERSION = 2;

// ============================================================================
// Session persistence (refresh-recovery)
// ============================================================================

// sessionStorage survives F5/Ctrl+R and window.location reloads, but is cleared
// when the tab closes — ideal for "don't lose my inputs on accidental reload"
// without the multi-tab / stale-data issues of localStorage.
const SESSION_STORAGE_KEY = 'trusscore.estimator.session.v1';

/**
 * Write the current snapshot to sessionStorage so it can be restored on
 * page reload. Silently ignores quota / serialization errors.
 */
function persistSnapshotToSession(data: EstimateDataObject): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Quota exceeded, private-mode storage disabled, etc. — nothing to do.
  }
}

/**
 * Remove any persisted session snapshot (e.g. after RESET).
 */
export function clearSessionSnapshot(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Ignore
  }
  _currentData = null;
}

/**
 * Result of attempting to hydrate from sessionStorage.
 */
export interface SessionHydrationResult {
  payload: LoadStatePayload;
  /** True when the saved snapshot had results (all steps completed) */
  autoCalculate: boolean;
}

/**
 * Attempt to load a previously-persisted snapshot from sessionStorage.
 * Returns null when no snapshot exists, when the stored JSON is invalid,
 * or when the snapshot describes a brand-new/empty wizard that isn't
 * worth restoring (user was still on the mode step with no progress).
 */
export function loadSessionSnapshot(): SessionHydrationResult | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;

  let raw: any;
  try {
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    raw = JSON.parse(stored);
  } catch {
    return null;
  }

  const { valid } = validateLoadedData(raw);
  if (!valid) {
    // Corrupt snapshot — clear it so we don't keep failing.
    try { window.sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch {}
    return null;
  }

  const completedSteps: WizardStep[] = Array.isArray(raw.wizard?.completedSteps)
    ? raw.wizard.completedSteps.filter((s: WizardStep) => VALID_STEPS.includes(s))
    : [];
  const hadResults = !!raw.wizard?.hasResults;

  // Don't bother restoring a pristine state — user had no progress to lose.
  if (!hadResults && completedSteps.length === 0 && raw.wizard?.currentStep === 'mode') {
    return null;
  }

  const STEP_ORDERS: Record<string, WizardStep[]> = {
    FullRoom: ['mode', 'walls', 'ceiling', 'dimensions', 'application'],
    CeilingOnly: ['mode', 'ceiling'],
    IndividualWalls: ['mode', 'walls', 'ceiling', 'application'],
  };
  const stepOrder = STEP_ORDERS[raw.calculationMode] || STEP_ORDERS.FullRoom;

  const effectiveCompleted: WizardStep[] = hadResults ? [...stepOrder] : completedSteps;
  const allComplete = hadResults || stepOrder.every(s => effectiveCompleted.includes(s));

  const targetStep: WizardStep = VALID_STEPS.includes(raw.wizard?.currentStep)
    ? raw.wizard.currentStep
    : stepOrder[0];

  const payload: LoadStatePayload = {
    calculationMode: raw.calculationMode,
    projectInfo: {
      projectName: raw.projectInfo?.projectName ?? '',
      customerName: raw.projectInfo?.customerName ?? '',
      email: raw.projectInfo?.email ?? '',
      phone: raw.projectInfo?.phone ?? '',
      location: raw.projectInfo?.location ?? '',
    },
    wallsConfig: {
      color: raw.wallsConfig.color,
      orientation: raw.wallsConfig.orientation,
      slatWallStartHeight: raw.wallsConfig.slatWallStartHeight ?? 0,
      slatWallNumPanels: raw.wallsConfig.slatWallNumPanels ?? 0,
      slatWallColor: raw.wallsConfig.slatWallColor ?? 'Gray',
      slatWallCoveragePercent: raw.wallsConfig.slatWallCoveragePercent ?? 0,
      slatWallEnabled: raw.wallsConfig.slatWallEnabled ?? false,
      usePresets: raw.wallsConfig.usePresets ?? true,
      selectedPresetId: raw.wallsConfig.selectedPresetId ?? null,
    },
    ceilingConfig: {
      include: raw.ceilingConfig?.include ?? true,
      color: raw.ceilingConfig?.color ?? 'White',
      zones: raw.ceilingConfig?.zones,
    },
    dimensionsConfig: {
      usePreset: raw.dimensionsConfig?.usePreset ?? true,
      presetId: raw.dimensionsConfig?.presetId,
      customDimensions: raw.dimensionsConfig?.customDimensions,
    },
    applicationConfig: {
      spaceType: raw.applicationConfig?.spaceType ?? 'garage',
      garage: {
        enabled: raw.applicationConfig?.garage?.enabled ?? false,
        width: raw.applicationConfig?.garage?.width ?? 9,
        height: raw.applicationConfig?.garage?.height ?? 7,
        numDoors: raw.applicationConfig?.garage?.numDoors ?? 1,
        onWallType: raw.applicationConfig?.garage?.onWallType ?? 'None',
      },
    },
    individualWalls: raw.individualWalls ?? undefined,
    currency: raw.currency,
    completedSteps: effectiveCompleted,
    currentStep: targetStep,
  };

  return { payload, autoCalculate: allComplete };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Resolve room dimensions from the wizard state, mirroring the logic in
 * EstimatorContext.buildEstimateInput so the snapshot always reflects what
 * the calculator would receive.
 */
function resolveRoomDimensions(
  calculationMode: CalculationMode,
  dimensionsConfig: { usePreset: boolean; presetId?: string; customDimensions?: RoomDimensions },
  ceilingZones?: CeilingZone[]
): RoomDimensions {
  if (calculationMode === 'CeilingOnly' && ceilingZones?.length) {
    const first = ceilingZones[0];
    return { length: first.length, width: first.width, height: 10 };
  }

  if (dimensionsConfig.usePreset && dimensionsConfig.presetId) {
    const preset = ROOM_PRESETS.find(p => p.id === dimensionsConfig.presetId);
    if (preset) {
      return { length: preset.length, width: preset.width, height: preset.height };
    }
  }

  if (dimensionsConfig.customDimensions) {
    return { ...dimensionsConfig.customDimensions };
  }

  // Fallback defaults (matches EstimatorContext)
  return { length: 20, width: 12, height: 10 };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Build a fresh snapshot from the current estimator state.
 * Call this from the EstimatorContext whenever state changes.
 */
export function syncEstimateData(state: {
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  calculationMode: CalculationMode;
  projectInfo: ProjectInfo;
  wallsConfig: {
    color: ProductColor;
    orientation: PanelOrientation;
    slatWallStartHeight: number;
    slatWallNumPanels: number;
    slatWallColor: ProductColor;
    slatWallCoveragePercent?: number;
    slatWallEnabled?: boolean;
    usePresets?: boolean;
    selectedPresetId?: string | null;
  };
  ceilingConfig: {
    include: boolean;
    color: ProductColor;
    zones?: CeilingZone[];
  };
  dimensionsConfig: {
    usePreset: boolean;
    presetId?: string;
    customDimensions?: RoomDimensions;
  };
  applicationConfig: {
    spaceType: string;
    garage: GarageConfig & { onWallType: 'None' | 'LengthWall' | 'WidthWall' };
  };
  individualWalls?: WallConfig[];
  currency?: Currency;
  estimateResult?: any;
}): EstimateDataObject {
  const data: EstimateDataObject = {
    schemaVersion: SCHEMA_VERSION,
    id: _currentData?.id ?? null,

    snapshotTimestamp: new Date().toISOString(),

    wizard: {
      currentStep: state.currentStep,
      completedSteps: [...state.completedSteps],
      hasResults: !!state.estimateResult,
    },

    calculationMode: state.calculationMode,

    projectInfo: { ...state.projectInfo },

    wallsConfig: {
      color: state.wallsConfig.color,
      orientation: state.wallsConfig.orientation,
      slatWallEnabled: state.wallsConfig.slatWallEnabled ?? state.wallsConfig.slatWallNumPanels > 0,
      slatWallStartHeight: state.wallsConfig.slatWallStartHeight,
      slatWallNumPanels: state.wallsConfig.slatWallNumPanels,
      slatWallColor: state.wallsConfig.slatWallColor,
      slatWallCoveragePercent: state.wallsConfig.slatWallCoveragePercent ?? 0,
      usePresets: state.wallsConfig.usePresets ?? true,
      selectedPresetId: state.wallsConfig.selectedPresetId ?? null,
    },

    ceilingConfig: {
      include: state.ceilingConfig.include,
      color: state.ceilingConfig.color,
      zones: state.ceilingConfig.zones
        ? state.ceilingConfig.zones.map(z => ({ ...z }))
        : undefined,
    },

    dimensionsConfig: {
      usePreset: state.dimensionsConfig.usePreset,
      presetId: state.dimensionsConfig.presetId,
      customDimensions: state.dimensionsConfig.customDimensions
        ? { ...state.dimensionsConfig.customDimensions }
        : undefined,
      resolvedDimensions: resolveRoomDimensions(
        state.calculationMode,
        state.dimensionsConfig,
        state.ceilingConfig.zones
      ),
    },

    applicationConfig: {
      spaceType: state.applicationConfig.spaceType,
      garage: { ...state.applicationConfig.garage },
    },

    individualWalls: state.individualWalls
      ? state.individualWalls.map(w => ({
          ...w,
          slatWall: { ...w.slatWall },
          garage: { ...w.garage },
        }))
      : undefined,

    currency: state.currency ?? 'USD',
  };

  _currentData = data;
  persistSnapshotToSession(data);
  return data;
}

/**
 * Return the most recent snapshot (or null if none yet).
 */
export function getEstimateData(): EstimateDataObject | null {
  return _currentData;
}

/**
 * Pretty-print the current data object to the browser console.
 * Intended for developer / QA testing.
 */
export function dumpEstimateData(): EstimateDataObject | null {
  if (!_currentData) {
    console.warn('[EstimateDataManager] No data captured yet. Navigate through the estimator first.');
    return null;
  }
  console.group('%c📋 Estimate Data Snapshot', 'font-weight:bold;font-size:14px;color:#2563eb');
  console.log('%cTimestamp:', 'font-weight:bold', _currentData.snapshotTimestamp);
  console.log('%cMode:', 'font-weight:bold', _currentData.calculationMode);
  console.log('%cStep:', 'font-weight:bold', _currentData.wizard.currentStep);
  console.log('%cResolved Dimensions:', 'font-weight:bold', _currentData.dimensionsConfig.resolvedDimensions);
  console.log('%cFull Data Object:', 'font-weight:bold');
  console.dir(_currentData, { depth: null });
  console.groupEnd();
  return _currentData;
}

// ============================================================================
// Validation
// ============================================================================

const VALID_MODES: CalculationMode[] = ['CeilingOnly', 'FullRoom', 'IndividualWalls'];
const VALID_COLORS: ProductColor[] = ['White', 'Gray'];
const VALID_ORIENTATIONS: PanelOrientation[] = ['Vertical', 'Horizontal'];
const VALID_CURRENCIES: Currency[] = ['USD', 'CAD'];
const VALID_STEPS: WizardStep[] = ['mode', 'walls', 'ceiling', 'dimensions', 'application', 'leadCapture'];

function validateLoadedData(data: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['File does not contain a valid JSON object.'] };
  }

  // Schema version
  if (typeof data.schemaVersion !== 'number') {
    errors.push('Missing or invalid schemaVersion.');
  } else if (data.schemaVersion > SCHEMA_VERSION) {
    errors.push(`File was saved with a newer schema version (${data.schemaVersion}). This app supports version ${SCHEMA_VERSION}.`);
  }

  // Calculation mode
  if (!VALID_MODES.includes(data.calculationMode)) {
    errors.push(`Invalid calculationMode: "${data.calculationMode}".`);
  }

  // Wizard
  if (!data.wizard || typeof data.wizard !== 'object') {
    errors.push('Missing wizard state.');
  } else {
    if (!VALID_STEPS.includes(data.wizard.currentStep)) {
      errors.push(`Invalid wizard.currentStep: "${data.wizard.currentStep}".`);
    }
    if (!Array.isArray(data.wizard.completedSteps)) {
      errors.push('wizard.completedSteps must be an array.');
    }
  }

  // Project info
  if (!data.projectInfo || typeof data.projectInfo !== 'object') {
    errors.push('Missing projectInfo.');
  }

  // Walls config
  if (!data.wallsConfig || typeof data.wallsConfig !== 'object') {
    errors.push('Missing wallsConfig.');
  } else {
    if (!VALID_COLORS.includes(data.wallsConfig.color)) errors.push('Invalid wallsConfig.color.');
    if (!VALID_ORIENTATIONS.includes(data.wallsConfig.orientation)) errors.push('Invalid wallsConfig.orientation.');
  }

  // Ceiling config
  if (!data.ceilingConfig || typeof data.ceilingConfig !== 'object') {
    errors.push('Missing ceilingConfig.');
  }

  // Dimensions config
  if (!data.dimensionsConfig || typeof data.dimensionsConfig !== 'object') {
    errors.push('Missing dimensionsConfig.');
  }

  // Application config
  if (!data.applicationConfig || typeof data.applicationConfig !== 'object') {
    errors.push('Missing applicationConfig.');
  }

  // Currency
  if (!VALID_CURRENCIES.includes(data.currency)) {
    errors.push(`Invalid currency: "${data.currency}".`);
  }

  // Individual walls (optional, but if present must be array)
  if (data.individualWalls !== undefined && data.individualWalls !== null && !Array.isArray(data.individualWalls)) {
    errors.push('individualWalls must be an array or undefined.');
  }

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// Save / Download
// ============================================================================

/**
 * Trigger the save flow. Opens the project-name modal if registered,
 * otherwise falls back to window.prompt (for console usage).
 *
 * When `options.onSave` is provided the singleton is bypassed entirely —
 * the caller is saving explicit data (e.g. the shared-estimate view).
 */
export function saveEstimateData(options?: SavePromptOptions): void {
  if (!options?.onSave && !_currentData) {
    console.warn('[EstimateDataManager] No data to save. Navigate through the estimator first.');
    return;
  }

  if (_savePrompt) {
    // Let the React modal handle the flow
    _savePrompt(options);
  } else {
    // Fallback for console usage when no modal is registered
    const existing =
      options?.defaultName ?? (_currentData?.projectInfo?.projectName?.trim() || '');
    const name = window.prompt('Enter a project name:', existing);
    if (name === null) return; // cancelled
    if (!name.trim()) {
      console.warn('[EstimateDataManager] Project name is required.');
      return;
    }
    if (options?.onSave) {
      options.onSave(name.trim());
    } else {
      performSave(name.trim());
    }
  }
}

/**
 * Internal: serialize the given EstimateDataObject and trigger a browser
 * download. Shared by the singleton save flow and the "save-with-explicit-data"
 * flow used on the shared-estimate view page.
 */
function downloadEstimateJson(data: EstimateDataObject, projectName: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeName = projectName
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 40) || 'estimate';
  const filename = `${safeName}_${timestamp}.trusscore`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);

  console.log(`[EstimateDataManager] Saved to "${filename}".`);
}

/**
 * Perform the actual save/download with the given project name.
 * Called by the SaveModal after the user confirms, or by the console fallback.
 */
export function performSave(projectName: string): void {
  if (!_currentData) {
    console.warn('[EstimateDataManager] No data to save.');
    return;
  }

  // Update the project name in the data object before saving
  _currentData.projectInfo.projectName = projectName;
  downloadEstimateJson(_currentData, projectName);
}

/**
 * Build an EstimateDataObject from a wizard-state payload without touching
 * the module singleton (`_currentData`). Used by the SharedEstimate view so
 * saving the viewed estimate doesn't overwrite the user's own in-progress
 * wizard state.
 *
 * `hasResults` is true by default because this function is only called in
 * contexts where the viewed estimate has already been calculated.
 */
export function buildEstimateDataFromWizardState(
  wizardState: WizardState & { currency?: Currency },
  projectName: string,
  hasResults: boolean = true,
): EstimateDataObject {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: null,
    snapshotTimestamp: new Date().toISOString(),
    wizard: {
      currentStep: wizardState.currentStep ?? 'mode',
      completedSteps: wizardState.completedSteps ? [...wizardState.completedSteps] : [],
      hasResults,
    },
    calculationMode: wizardState.calculationMode,
    projectInfo: { ...wizardState.projectInfo, projectName },
    wallsConfig: {
      color: wizardState.wallsConfig.color,
      orientation: wizardState.wallsConfig.orientation,
      slatWallEnabled:
        wizardState.wallsConfig.slatWallEnabled ??
        (wizardState.wallsConfig.slatWallNumPanels ?? 0) > 0,
      slatWallStartHeight: wizardState.wallsConfig.slatWallStartHeight ?? 0,
      slatWallNumPanels: wizardState.wallsConfig.slatWallNumPanels ?? 0,
      slatWallColor: wizardState.wallsConfig.slatWallColor ?? 'Gray',
      slatWallCoveragePercent: wizardState.wallsConfig.slatWallCoveragePercent ?? 0,
      usePresets: wizardState.wallsConfig.usePresets ?? true,
      selectedPresetId: wizardState.wallsConfig.selectedPresetId ?? null,
    },
    ceilingConfig: {
      include: wizardState.ceilingConfig.include ?? true,
      color: wizardState.ceilingConfig.color ?? 'White',
      zones: wizardState.ceilingConfig.zones
        ? wizardState.ceilingConfig.zones.map((z: CeilingZone) => ({ ...z }))
        : undefined,
    },
    dimensionsConfig: {
      usePreset: wizardState.dimensionsConfig.usePreset ?? true,
      presetId: wizardState.dimensionsConfig.presetId,
      customDimensions: wizardState.dimensionsConfig.customDimensions
        ? { ...wizardState.dimensionsConfig.customDimensions }
        : undefined,
      resolvedDimensions: resolveRoomDimensions(
        wizardState.calculationMode,
        wizardState.dimensionsConfig,
        wizardState.ceilingConfig.zones,
      ),
    },
    applicationConfig: {
      spaceType: wizardState.applicationConfig.spaceType ?? 'garage',
      garage: { ...wizardState.applicationConfig.garage },
    },
    individualWalls: wizardState.individualWalls
      ? wizardState.individualWalls.map(w => ({
          ...w,
          slatWall: { ...w.slatWall },
          garage: { ...w.garage },
        }))
      : undefined,
    currency: wizardState.currency ?? 'USD',
  };
}

/**
 * Download an explicit EstimateDataObject under the given project name,
 * bypassing the module singleton. Pair with `buildEstimateDataFromWizardState`
 * when saving viewed (shared) estimates.
 */
export function performSaveWithData(data: EstimateDataObject, projectName: string): void {
  const withName: EstimateDataObject = {
    ...data,
    projectInfo: { ...data.projectInfo, projectName },
  };
  downloadEstimateJson(withName, projectName);
}

// ============================================================================
// Load / Import
// ============================================================================

/**
 * Options for loadEstimateData.
 */
export interface LoadEstimateOptions {
  /**
   * Fires once after the state has been dispatched to the EstimatorContext.
   * Use this to navigate to `/` when the caller isn't already on the wizard
   * route — otherwise the loaded state lands in context but the user stays
   * on whatever page triggered the load (e.g. the shared-estimate view).
   */
  afterLoad?: () => void;
}

/**
 * Open a file picker, validate the selected JSON, confirm with the user,
 * and hydrate the app state from the file.
 *
 * If all wizard steps were completed the estimate will auto-calculate
 * after loading.
 */
export function loadEstimateData(options?: LoadEstimateOptions): void {
  if (!_stateLoader) {
    console.error('[EstimateDataManager] State loader not registered. Is the EstimatorProvider mounted?');
    return;
  }

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.trusscore,.json,application/json';
  input.style.display = 'none';
  document.body.appendChild(input);

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    document.body.removeChild(input);

    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = JSON.parse(reader.result as string);
        const { valid, errors } = validateLoadedData(raw);

        if (!valid) {
          console.error('[EstimateDataManager] Validation failed:', errors);
          // The bullet list is inside the message rather than glued on outside
          // it, so a translator can move or re-punctuate the lead-in.
          alert(tr('common:error.invalidFile', { details: errors.join('\n• ') }));
          return;
        }

        // Build a summary for the confirmation prompt
        const mode = raw.calculationMode;
        const dims = raw.dimensionsConfig?.resolvedDimensions;
        const dimsStr = dims ? `${dims.length}'L × ${dims.width}'W × ${dims.height}'H` : 'N/A';
        const project = raw.projectInfo?.projectName || '(unnamed)';
        const stepsComplete = raw.wizard?.completedSteps?.length ?? 0;

        const confirmInfo: LoadConfirmInfo = {
          projectName: project,
          mode,
          dimensions: dimsStr,
          stepsComplete,
        };

        // Perform the actual state hydration
        const proceedWithLoad = () => {
          // Determine the step to navigate to
          const STEP_ORDERS: Record<string, WizardStep[]> = {
            FullRoom: ['mode', 'walls', 'ceiling', 'dimensions', 'application'],
            CeilingOnly: ['mode', 'ceiling'],
            IndividualWalls: ['mode', 'walls', 'ceiling', 'application'],
          };
          const stepOrder = STEP_ORDERS[raw.calculationMode] || STEP_ORDERS.FullRoom;

          const hadResults = !!raw.wizard.hasResults;
          const completedSteps: WizardStep[] = hadResults
            ? [...stepOrder]
            : (raw.wizard.completedSteps || []).filter(
                (s: WizardStep) => VALID_STEPS.includes(s)
              );
          const allComplete = hadResults || stepOrder.every((s: WizardStep) => completedSteps.includes(s));

          const targetStep: WizardStep = VALID_STEPS.includes(raw.wizard.currentStep)
            ? raw.wizard.currentStep
            : stepOrder[0];

          const payload: LoadStatePayload = {
            calculationMode: raw.calculationMode,
            projectInfo: {
              projectName: raw.projectInfo.projectName ?? '',
              customerName: raw.projectInfo.customerName ?? '',
              email: raw.projectInfo.email ?? '',
              phone: raw.projectInfo.phone ?? '',
              location: raw.projectInfo.location ?? '',
            },
            wallsConfig: {
              color: raw.wallsConfig.color,
              orientation: raw.wallsConfig.orientation,
              slatWallStartHeight: raw.wallsConfig.slatWallStartHeight ?? 0,
              slatWallNumPanels: raw.wallsConfig.slatWallNumPanels ?? 0,
              slatWallColor: raw.wallsConfig.slatWallColor ?? 'Gray',
              slatWallCoveragePercent: raw.wallsConfig.slatWallCoveragePercent ?? 0,
              slatWallEnabled: raw.wallsConfig.slatWallEnabled ?? false,
              usePresets: raw.wallsConfig.usePresets ?? true,
              selectedPresetId: raw.wallsConfig.selectedPresetId ?? null,
            },
            ceilingConfig: {
              include: raw.ceilingConfig.include ?? true,
              color: raw.ceilingConfig.color ?? 'White',
              zones: raw.ceilingConfig.zones,
            },
            dimensionsConfig: {
              usePreset: raw.dimensionsConfig.usePreset ?? true,
              presetId: raw.dimensionsConfig.presetId,
              customDimensions: raw.dimensionsConfig.customDimensions,
            },
            applicationConfig: {
              spaceType: raw.applicationConfig.spaceType ?? 'garage',
              garage: {
                enabled: raw.applicationConfig.garage?.enabled ?? false,
                width: raw.applicationConfig.garage?.width ?? 9,
                height: raw.applicationConfig.garage?.height ?? 7,
                numDoors: raw.applicationConfig.garage?.numDoors ?? 1,
                onWallType: raw.applicationConfig.garage?.onWallType ?? 'None',
              },
            },
            individualWalls: raw.individualWalls ?? undefined,
            currency: raw.currency,
            completedSteps,
            currentStep: targetStep,
          };

          _stateLoader!(payload, allComplete);
          options?.afterLoad?.();

          console.log(
            `[EstimateDataManager] Loaded "${project}" — navigated to step "${targetStep}"` +
            (allComplete ? ' (auto-calculating)' : '')
          );
        };

        // Use custom modal if registered, otherwise fall back to native confirm
        if (_loadConfirm) {
          _loadConfirm(confirmInfo).then(confirmed => {
            if (confirmed) {
              proceedWithLoad();
            } else {
              console.log('[EstimateDataManager] Load cancelled by user.');
            }
          });
        } else {
          const msg =
            `Load this estimate?\n\n` +
            `  Project:    ${confirmInfo.projectName}\n` +
            `  Mode:       ${confirmInfo.mode}\n` +
            `  Dimensions: ${confirmInfo.dimensions}\n` +
            `  Steps done: ${confirmInfo.stepsComplete}\n\n` +
            `This will replace all current data in the estimator.`;

          if (!window.confirm(msg)) {
            console.log('[EstimateDataManager] Load cancelled by user.');
            return;
          }
          proceedWithLoad();
        }
      } catch (err) {
        console.error('[EstimateDataManager] Failed to parse file:', err);
        alert(tr('common:error.unreadableFile'));
      }
    };

    reader.readAsText(file);
  });

  input.click();
}

// ============================================================================
// Global console accessors
// ============================================================================

if (typeof window !== 'undefined') {
  (window as any).__dumpEstimateData = dumpEstimateData;
  (window as any).__getEstimateData = getEstimateData;
  (window as any).__saveEstimateData = saveEstimateData;
  (window as any).__loadEstimateData = loadEstimateData;
}

